import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
} from '@mui/material';

import AutoCompleteSelect from '@/components/common/AutoCompleteSelect';
import TreeSelect from '@/components/common/TreeSelect';
import { transformToTreeStructure } from '@/utils/treeUtils';
import { useCreateManageRoleMutation, useGetPermissionsQuery } from '../../store/api/roleApi';
import { useGetLocationsDropdownQuery } from '../../store/api/locationApi';
import assetService from '../../services/assetService';
import api from '../../services/api';
import { useToast } from '../common/Toast/ToastProvider';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const AddRoleForm: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { showToast } = useToast();
  const [createManageRole, { isLoading: isRoleLoading }] =
    useCreateManageRoleMutation();

  /* ---------------- BASIC FIELDS ---------------- */

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const [roleErrors, setRoleErrors] = useState<any>({});

  // Debounced values
    const [debouncedCategorySearch, setDebouncedCategorySearch] = useState('');
    const [debouncedLocationSearch, setDebouncedLocationSearch] = useState('');
    const [debouncedStatusSearch, setDebouncedStatusSearch] = useState('');
    const [debouncedPermissionSearch, setDebouncedPermissionSearch] = useState('');

    useEffect(() => {
        if (open) {
            // Reset basic fields
            setName('');
            setDescription('');
            setPermissionIds([]);
            setCategoryIds([]);
            setLocationIds([]);
            setStatusIds([]);
            setRoleErrors({});

            // Reset category state
            setCategorySearch('');
            setCategoryPage(1);
            setAllCategories([]);
            setHasMoreCategories(true);

            // Reset location state
            setLocationState({
            search: '',
            page: 1,
            data: [],
            hasMore: true,
            });

            // Reset status state
            setStatusSearch('');
            setStatusPage(1);
            setAllStatuses([]);
        }
        }, [open]);

  /* ---------------- PERMISSIONS ---------------- */

  const [permissionSearch, setPermissionSearch] = useState('');
  const [permissionIds, setPermissionIds] = useState<string[]>([]);
  const [allPermissions, setAllPermissions] = useState<any[]>([]);

  const {
    data: permissions = [],
    isLoading: isLoadingPermissions,
    isFetching: isPermissionsFetching,
  } = useGetPermissionsQuery();

  useEffect(() => {
    if (permissions && Array.isArray(permissions)) {
      setAllPermissions(permissions);
    }
  }, [permissions]);

  const permissionOptions = useMemo(
    () =>
      allPermissions
        .filter((p) => {
          if (!debouncedPermissionSearch) return true;
          const name = p.name;
          const desc = p.description;
          return (
            name?.toLowerCase().includes(debouncedPermissionSearch.toLowerCase()) ||
            desc?.toLowerCase().includes(debouncedPermissionSearch.toLowerCase())
          );
        })
        .map((p) => ({
          label: p.description || p.name,
          value: p.id,
        })),
    [allPermissions, debouncedPermissionSearch]
  );

  /* ---------------- CATEGORIES ---------------- */

  const [categorySearch, setCategorySearch] = useState('');
  const [categoryPage, setCategoryPage] = useState(1);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [hasMoreCategories, setHasMoreCategories] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [categoryIds, setCategoryIds] = useState<string[]>([]);

  useEffect(() => {
    if (!open) return;

    const fetchCategories = async () => {
        setIsLoadingCategories(true);
        try {
        const data = await assetService.getAssetCategoriesDropdown(
            debouncedCategorySearch,
        );

        const formatted = Array.isArray(data)
            ? data.map((c: any) => ({
                id: c.id,
                name: c.name,
                parent_category: c.parent_category,
            }))
            : [];

        setAllCategories((prev) =>
            categoryPage === 1 ? formatted : [...prev, ...formatted]
        );

        setHasMoreCategories(formatted.length === 20);
        } catch {
        showToast('Failed to load categories', 'error');
        } finally {
        setIsLoadingCategories(false);
        }
    };

    fetchCategories();
    }, [open, debouncedCategorySearch, categoryPage]);

  const categoryTree = useMemo(
    () => transformToTreeStructure(allCategories),
    [allCategories]
  );

  /* ---------------- LOCATIONS ---------------- */

  const [locationState, setLocationState] = useState({
    search: '',
    page: 1,
    data: [] as any[],
    hasMore: true,
  });

  const [locationIds, setLocationIds] = useState<string[]>([]);

  const {
    data: locationsDataRole,
    isFetching: isLocationsFetchingRole,
    isLoading: isLocationsLoadingRole,
  } = useGetLocationsDropdownQuery({
    page: locationState.page,
    limit: 25,
    search: debouncedLocationSearch,
  });

  useEffect(() => {
    if (!locationsDataRole?.results) return;

    setLocationState((prev) => {
        const newData =
        prev.page === 1
            ? locationsDataRole.results
            : [
                ...prev.data,
                ...locationsDataRole.results.filter(
                (item: any) =>
                    !prev.data.some((existing) => existing.id === item.id)
                ),
            ];

        return {
        ...prev,
        data: newData,
        hasMore: !!locationsDataRole.next,
        };
    });
    }, [locationsDataRole]);

  const locationTree = useMemo(() => {
    if (!locationState.data.length) return [];
    return transformToTreeStructure(
      locationState.data.map((loc: any) => ({
        id: loc.id,
        name: loc.name,
        parent_location: loc.parent_location || null,
      }))
    );
  }, [locationState.data]);

  /* ---------------- STATUSES ---------------- */

  const [statusSearch, setStatusSearch] = useState('');
  const [statusPage, setStatusPage] = useState(1);
  const [allStatuses, setAllStatuses] = useState<any[]>([]);
  const [, setHasMoreStatuses] = useState(true);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [statusIds, setStatusIds] = useState<string[]>([]);

  useEffect(() => {
    const fetchStatuses = async () => {
      setIsLoadingStatuses(true);
      try {
        const response = await api.get('/assets/addon/asset-statuses/', {
          params: { dropdown: true, search: debouncedStatusSearch, page: statusPage, limit: 20 },
        });

        const data = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        if (statusPage === 1) setAllStatuses(data);
        else setAllStatuses((prev) => [...prev, ...data]);

        setHasMoreStatuses(!!response.data?.next || data.length === 20);
      } catch {
        showToast('Failed to load statuses', 'error');
      } finally {
        setIsLoadingStatuses(false);
      }
    };

    if (open) fetchStatuses();
  }, [open, debouncedStatusSearch, statusPage]);

  const statusOptions = useMemo(
    () =>
      allStatuses.map((s: any) => ({
        label: s.name,
        value: s.id,
      })),
    [allStatuses]
  );


    // Category debounce
    useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedCategorySearch(categorySearch);
    }, 500);

    return () => clearTimeout(timer);
    }, [categorySearch]);

    // Location debounce
    useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedLocationSearch(locationState.search);
    }, 500);

    return () => clearTimeout(timer);
    }, [locationState.search]);

    // Status debounce
    useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedStatusSearch(statusSearch);
    }, 500);

    return () => clearTimeout(timer);
    }, [statusSearch]);

    // Permission debounce (optional - client filter)
    useEffect(() => {
    const timer = setTimeout(() => {
        setDebouncedPermissionSearch(permissionSearch);
    }, 300);

    return () => clearTimeout(timer);
    }, [permissionSearch]);

  /* ---------------- VALIDATION ---------------- */

  const validateRoleForm = () => {
    const newErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      newErrors.name = 'Role name is required';
      isValid = false;
    }

    setRoleErrors(newErrors);
    return isValid;
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setPermissionIds([]);
    setCategoryIds([]);
    setLocationIds([]);
    setStatusIds([]);
    setRoleErrors({});
    };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRoleForm()) return;

    try {
      await createManageRole({
        roleData: {
          name,
          description,
          permission_ids: permissionIds,
          category_ids: categoryIds,
          location_ids: locationIds,
          status_ids: statusIds,
        },
      }).unwrap();

      showToast('Role created successfully', 'success');

      resetForm();
      onSuccess();
      onClose();
    } catch {
      showToast('Failed to create role', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add Role</DialogTitle>
      <DialogContent dividers>
        <form onSubmit={handleSubmit}>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink>Role Name*</InputLabel>
                <TextField
                    fullWidth
                    size="small"
                    label="Role Name*"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    error={!!roleErrors.name}
                    helperText={roleErrors.name}
                    sx={{ mb: 2 }}
                />
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink>Description</InputLabel>
                <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={3}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    sx={{ mb: 3 }}
                />
            </FormControl>

            {/* Permissions */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink>Permissions</InputLabel>
                <AutoCompleteSelect
                fullwidth
                multiple
                options={permissionOptions}
                value={permissionIds}
                loading={isLoadingPermissions || isPermissionsFetching}
                onChange={(value) => setPermissionIds(value as string[])}
                label="Permissions"
                inputValue={permissionSearch}
                onInputChange={setPermissionSearch}
                />
            </FormControl>

            {/* Categories */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel shrink>Categories</InputLabel>
                <TreeSelect
                    label="Categories"
                    items={categoryTree}
                    value={categoryIds}
                    multiple
                    searchable
                    searchValue={categorySearch}
                    onSearchChange={(val) => {
                    setCategorySearch(val);
                    setAllCategories([]);
                    setCategoryPage(1);
                    }}
                    onScrollEnd={() =>
                    hasMoreCategories &&
                    !isLoadingCategories &&
                    setCategoryPage((prev) => prev + 1)
                    }
                    onChange={(value) =>
                    setCategoryIds(Array.isArray(value) ? value : [value])
                    }
                    isLoading={isLoadingCategories}
                />
                </FormControl>

            {/* Locations */}
            <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel shrink>Locations</InputLabel>
                <TreeSelect
                    label="Locations"
                    items={locationTree}
                    value={locationIds}
                    multiple
                    searchable
                    searchValue={locationState.search}
                    onSearchChange={(val) =>
                    setLocationState((prev) => ({
                        ...prev,
                        search: val,
                        page: 1,
                        data: [],
                    }))
                    }
                    onScrollEnd={() =>
                    locationState.hasMore &&
                    !isLocationsFetchingRole &&
                    setLocationState((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                    }))
                    }
                    onChange={(value) =>
                    setLocationIds(Array.isArray(value) ? value : [value])
                    }
                    isLoading={isLocationsLoadingRole}
                />
            </FormControl>

            {/* Statuses */}
            <FormControl fullWidth sx={{ mt: 2 }}>
                <AutoCompleteSelect
                fullwidth
                multiple
                options={statusOptions}
                value={statusIds}
                loading={isLoadingStatuses}
                onChange={(value) => setStatusIds(value as string[])}
                label="Statuses"
                inputValue={statusSearch}
                onInputChange={(val) => {
                  setStatusSearch(val);
                  setStatusPage(1);
                  setAllStatuses([]);
                }}
                />
            </FormControl>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isRoleLoading}
              startIcon={
                isRoleLoading ? <CircularProgress size={20} /> : null
              }
            >
              Create Role
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddRoleForm;